import type { routes } from '@semios/app-platform-banyan-route-definitions'
import type {
  TLngLat,
  TValuesCurrentBlocks,
  TValuesCurrentHeatmapPoints,
  TValuesCurrentPoints,
  TValuesCurrentProperties,
  TValuesCurrentRegions,
} from 'App/Map/types'
import { isRegionDataEnabled } from 'utils/useIsRegionDataEnabled'
import type { TCurrentValuesMapProcessedCaches } from '../_types'
import { getPropertyIdsForAllPropertiesInVisibleRegions } from './getPropertyIdsForAllPropertiesInVisibleRegions'

export const generateUsualStyleAPIArgs = ({
  heatmapExtremesForAllPropertiesInVisibleRegions,
  processedCaches,
  block,
  inBlockPoint,
  outOfBlockPoint,
  property,
  trapPoint,
  region,
}: {
  heatmapExtremesForAllPropertiesInVisibleRegions: boolean
  processedCaches: TCurrentValuesMapProcessedCaches
  block?: {
    valueType: TValuesCurrentBlocks
  }
  inBlockPoint?: {
    valueType: TValuesCurrentPoints | TValuesCurrentHeatmapPoints
  }
  outOfBlockPoint?: {
    valueType: TValuesCurrentPoints
  }
  property?: {
    valueType: TValuesCurrentProperties
  }
  trapPoint?: {
    valueType: TValuesCurrentPoints
  }
  region?: {
    valueType: TValuesCurrentRegions
  }
}): routes.ValuesCurrent.Request => {
  const returner: routes.ValuesCurrent.Request = {}

  if (block && !!processedCaches.blockValues.itemsWithinView.length) {
    returner.blocks = {
      blockIds: processedCaches.blockValues.itemsWithinView.map((b) => Number(b.id)),
      values: { [block.valueType]: true },
    }
  }

  if (inBlockPoint && !!processedCaches.blockScdsValues.itemsWithinView.length) {
    returner.heatmap_points = {
      blockIds: processedCaches.blockScdsValues.itemsWithinView.map((b) => Number(b.id)),
      values: { [inBlockPoint.valueType]: true },
    }
  }

  if (property && !!processedCaches.propertyValues.itemsWithinView.length) {
    const propertyIds = heatmapExtremesForAllPropertiesInVisibleRegions
      ? getPropertyIdsForAllPropertiesInVisibleRegions(
          processedCaches.propertyValues.itemsWithinView.map((p) => Number(p.id)),
        )
      : processedCaches.propertyValues.itemsWithinView.map((p) => Number(p.id))

    returner.properties = {
      propertyIds,
      values: { [property.valueType]: true },
    }
  }

  if (region && isRegionDataEnabled()) {
    const regionIds = processedCaches.regionValues.itemsWithinView.map((r) => r.id)

    if (regionIds.length) {
      returner.regions = {
        regionIds: regionIds,
        values: {
          [region.valueType]: true,
        },
      }
    }
  }

  const shouldFetchPoints = inBlockPoint || outOfBlockPoint || trapPoint

  if (shouldFetchPoints) {
    const lngLatsToFetch: TLngLat[] = []
    const pointsValuesToFetch: NonNullable<routes.ValuesCurrent.Request['points']>['values'] = {}

    if (inBlockPoint && !!processedCaches.stationValues.itemsWithinView.length) {
      processedCaches.stationValues.itemsWithinView.forEach((s) => {
        lngLatsToFetch.push(s.meta.lngLat)
      })

      pointsValuesToFetch[inBlockPoint.valueType] = true
    }

    if (outOfBlockPoint && !!processedCaches.stationValues.itemsWithinView.length) {
      processedCaches.stationValues.itemsWithinView.forEach((s) => {
        lngLatsToFetch.push(s.meta.lngLat)
      })

      pointsValuesToFetch[outOfBlockPoint.valueType] = true
    }

    if (inBlockPoint && !!processedCaches.windMachineValues.itemsWithinView.length) {
      processedCaches.windMachineValues.itemsWithinView.forEach((s) => {
        lngLatsToFetch.push(s.meta.lngLat)
      })

      pointsValuesToFetch[inBlockPoint.valueType] = true
    }

    if (outOfBlockPoint && !!processedCaches.windMachineValues.itemsWithinView.length) {
      processedCaches.windMachineValues.itemsWithinView.forEach((s) => {
        lngLatsToFetch.push(s.meta.lngLat)
      })

      pointsValuesToFetch[outOfBlockPoint.valueType] = true
    }

    if (trapPoint && !!processedCaches.trapsValues.itemsWithinView.length) {
      processedCaches.trapsValues.itemsWithinView.forEach((t) => {
        lngLatsToFetch.push(t.meta.lngLat)
      })

      pointsValuesToFetch[trapPoint.valueType] = true
    }

    if (!!lngLatsToFetch.length) {
      returner.points = {
        lngLats: lngLatsToFetch,
        values: pointsValuesToFetch,
      }
    }
  }

  return returner
}
